/* eslint-disable prettier/prettier */
import { ApiService } from '../../services/apiService';
import {
  GET_MEASUREMENT_CARD,
  GET_STACKS,
  REMOVE_PICKED_STACK_NUMBER,
  StackActionTypes,
  UPDATE_RESERVED_STACK_NUMBERS,
  CREATE_STACK,
  UPLOAD_STACK,
  UPLOAD_STACK_FAILED,
  UPLOAD_STACK_SUCCESS,
  CLEAR_STACKS,
  CLEAR_FIELD_VALUES,
  UPDATE_FIELD_VALUE,
  SET_FIELD_VALUES,
  ClearStacksAction,
  ClearFieldValuesAction,
  UpdateFieldValueAction,
  SetFieldValuesAction,
} from './types';
import { notificationActions } from '../notification/actions';
import { Message, syncFailed } from '../notification/types';
import {
  ListStacksResponseItem,
  MeasurementCardResponse,
} from '../../.generated/admin-api';
import { FieldValueState } from '../../components/registrationFlow';

import { ApplicationState, AppDispatch } from '..';
import { authActions } from '../auth/actions';

function clearStacks(): ClearStacksAction {
  return { type: CLEAR_STACKS };
}

function clearFieldValues(departmentId: string): ClearFieldValuesAction {
  return { type: CLEAR_FIELD_VALUES, departmentId: departmentId };
}

function updateFieldValue(
  departmentId: string,
  field: FieldValueState,
): UpdateFieldValueAction {
  return {
    type: UPDATE_FIELD_VALUE,
    departmentId: departmentId,
    field: field,
  };
}

function setFieldValues(
  departmentId: string,
  values: FieldValueState[],
): SetFieldValuesAction {
  return {
    type: SET_FIELD_VALUES,
    departmentId,
    values,
  };
}

function loadStackValues(departmentId: string, stackId: string) {
  return async (
    dispatch: AppDispatch,
    getState: () => ApplicationState,
  ) => {
    dispatch(clearFieldValues(departmentId));
    try {
      const state = getState();
      const localStack =
        state.stacks?.departments?.[departmentId]?.localStacks?.[stackId];

      if (localStack) {
        return dispatch(
          setFieldValues(
            departmentId,
            Object.keys(localStack.fieldValues).map((fieldId) => ({
              fieldId: fieldId,
              value: localStack.fieldValues[fieldId],
              isValid: true, // assume valid since it has been validated during creation.
            })),
          ),
        );
      }

      await dispatch(authActions.refreshTokenIfExpired());
      const response = await ApiService.getStackById(stackId);
      return dispatch(
        setFieldValues(
          departmentId,
          Object.keys(response.data.values).map((fieldId) => ({
            fieldId: fieldId,
            value: response.data.values[fieldId],
            isValid: true, // assume valid since it has been fetched from backend
          })),
        ),
      );
    } catch {
      dispatch(setFieldValues(departmentId, []));
      return dispatch(notificationActions.addMessage(syncFailed));
    }
  };
}

function getStacksByDepartmentId(departmentId: string) {
  function success(
    stacks: ListStacksResponseItem[],
    departmentId: string,
  ): StackActionTypes {
    return {
      type: GET_STACKS,
      stacks,
      departmentId,
    };
  }

  return async (dispatch: AppDispatch, getState: () => ApplicationState) => {
    // also filter by from date and registrator
    const from = new Date();
    from.setMonth(from.getMonth() - 2);
    const registrator = getState().user.username;
    try {
      await dispatch(authActions.refreshTokenIfExpired());
      const response = await ApiService.getStackList({
        departmentIds: [departmentId],
        from: from.toISOString(),
        registrators: registrator ? [registrator] : undefined,
        page: 1,
        pageSize: 50,
      });
      return dispatch(success(response.data.data, departmentId));
    } catch {
      return dispatch(notificationActions.addMessage(syncFailed));
    }
  };
}

function getMeasurementCardByDepartmentId(departmentId: string) {
  function success(
    departmentId: string,
    measurementCard: MeasurementCardResponse,
  ): StackActionTypes {
    return {
      type: GET_MEASUREMENT_CARD,
      departmentId,
      measurementCard,
    };
  }

  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(authActions.refreshTokenIfExpired());
      const response = await ApiService.getDepartmentMeasurementCard(
        departmentId,
      );
      const measurementcard = response.data;
      return dispatch(success(departmentId, measurementcard));
    } catch {
      return dispatch(notificationActions.addMessage(syncFailed));
    }
  };
}

function uploadStack(departmentId: string, localStackId: string) {
  return async (dispatch: AppDispatch, getState: () => ApplicationState) => {
    const state = getState();
    const localStack =
      state.stacks.departments[departmentId]?.localStacks?.[localStackId];
    if (localStack) {
      // signal upload stack begin
      dispatch({
        type: UPLOAD_STACK,
        departmentId: departmentId,
        localStackId: localStackId,
      });
      try {
        await dispatch(authActions.refreshTokenIfExpired());
        await ApiService.createStack(
          departmentId,
          localStack.measurementCardId,
          localStack.fieldValues,
        );
        dispatch({
          type: UPLOAD_STACK_SUCCESS,
          departmentId: departmentId,
          localStackId: localStackId,
        });
      } catch {
        dispatch({
          type: UPLOAD_STACK_FAILED,
          departmentId: departmentId,
          localStackId: localStackId,
        });
      }
    }
  };
}

function uploadAllLocalStacksForDepartmentId(departmentId: string) {
  return async (dispatch: AppDispatch, getState: () => ApplicationState) => {
    const state = getState();
    const department = state.stacks.departments[departmentId];
    if (department && department.localStacks) {
      return Promise.all(
        Object.keys(department.localStacks).map((localStackId) =>
          dispatch(uploadStack(departmentId, localStackId)),
        ),
      );
    }
  };
}

function createStack(
  departmentId: string,
  measurementCardId: string,
  fieldValues: FieldValueState[],
  stackNumber: string,
  date: string,
) {
  return async (dispatch: AppDispatch) => {
    const values: Record<string, unknown> = {};
    for (const fieldValue of fieldValues) {
      values[fieldValue.fieldId] = fieldValue.value;
    }
    dispatch({
      type: CREATE_STACK,
      departmentId: departmentId,
      measurementCardId: measurementCardId,
      fieldValues: values, // TODO: could take the field values from state.stacks.departments[id].fieldValues;
      stackNumber: stackNumber,
      date: date,
    });
    const parsedStackNumber = parseInt(stackNumber, 10); // stackNumber is delivered to createStack as string. Number is required by removePickedStackNumber
    await dispatch(
      stackActions.removePickedStackNumber(departmentId, parsedStackNumber),
    );
    dispatch(clearFieldValues(departmentId));
    dispatch(
      notificationActions.addMessage(
        Message.success('registration_page.created').withTranslatedText(),
      ),
    );
    // try to upload and refresh
    await dispatch(uploadAllLocalStacksForDepartmentId(departmentId));
    return dispatch(getStacksByDepartmentId(departmentId));
  };
}

function updateReservedStackNumbers(departmentId: string, deviceId: string) {
  function success(reservedStackNumbers: number[]): StackActionTypes {
    return {
      type: UPDATE_RESERVED_STACK_NUMBERS,
      departmentId,
      reservedStackNumbers,
    };
  }

  return async (dispatch: AppDispatch) => {
    try {
      await dispatch(authActions.refreshTokenIfExpired());
      const response = await ApiService.reserveStackNumbers(
        departmentId,
        deviceId,
      );
      return dispatch(success(response.data));
    } catch {
      return dispatch(notificationActions.addMessage(syncFailed));
    }
  };
}

function removePickedStackNumber(departmentId: string, stackNumber: number) {
  return (dispatch: AppDispatch) => {
    const action: StackActionTypes = {
      type: REMOVE_PICKED_STACK_NUMBER,
      departmentId,
      stackNumber,
    };
    dispatch(action);
  };
}

export const stackActions = {
  getStacksByDepartmentId,
  getMeasurementCardByDepartmentId,
  updateReservedStackNumbers,
  removePickedStackNumber,
  createStack,
  uploadStack,
  uploadAllLocalStacksForDepartmentId,
  clearStacks,
  clearFieldValues,
  updateFieldValue,
  loadStackValues,
};
